import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { statusBadge } from './editorial';

const EMPTY = {
  text: '', options: ['', '', '', ''], correct_index: 0, explanation: '',
  difficulty: 'media', subject_id: '', contest_ids: [], status: 'PUBLISHED',
};

export default function AdminQuiz() {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [contests, setContests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get('/admin/questions').then(({ data }) => setItems(data.items)).catch(() => {});
    api.get('/admin/subjects').then(({ data }) => setSubjects(data.items)).catch(() => {});
    api.get('/admin/contests').then(({ data }) => setContests(data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const csvRef = useRef(null);

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/quiz/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.errors.length) toast.warning(`Importate ${data.created} domande, ${data.errors.length} errori: ${data.errors[0]}`);
      else toast.success(`Importate ${data.created} domande`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore importazione');
    } finally { e.target.value = ''; }
  };

  const save = async () => {
    const payload = { ...form, options: form.options.filter((o) => o.trim()), correct_index: Number(form.correct_index) };
    if (payload.options.length < 2) { toast.error('Servono almeno 2 risposte'); return; }
    try {
      if (editing) await api.put(`/admin/questions/${editing}`, payload);
      else await api.post('/admin/questions', payload);
      toast.success('Domanda salvata');
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare la domanda?')) return;
    await api.delete(`/admin/questions/${id}`);
    load();
  };

  const subjectName = (id) => subjects.find((s) => s.subject_id === id)?.name || '—';

  return (
    <div data-testid="admin-quiz">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-semibold text-lg text-slate-900">Banca domande ({items.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => csvRef.current?.click()} data-testid="question-import"
            className="rounded-full">
            <Upload size={15} strokeWidth={1.5} className="mr-1.5" /> Importa CSV
          </Button>
          <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="question-import-file" />
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="question-create"
            className="rounded-full bg-brand hover:bg-brand-hover text-white">
            <Plus size={15} strokeWidth={1.5} className="mr-1.5" /> Nuova domanda
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4 -mt-3">
        Formato CSV: <code className="bg-slate-100 px-1 rounded">testo;opzione1;opzione2;opzione3;opzione4;corretta(1-4);spiegazione;difficoltà;subject_id;contest_ids(a|pipe)</code>
      </p>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {['Domanda', 'Materia', 'Difficoltà', 'Concorsi', 'Stato', ''].map((h) => (
                <th key={h} className="px-5 py-3.5 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((q) => (
              <tr key={q.question_id} data-testid={`question-row-${q.question_id}`}>
                <td className="px-5 py-3.5 text-slate-700 max-w-[280px] truncate">{q.text}</td>
                <td className="px-5 py-3.5 text-slate-600">{subjectName(q.subject_id)}</td>
                <td className="px-5 py-3.5 text-slate-600 capitalize">{q.difficulty}</td>
                <td className="px-5 py-3.5 text-slate-600">{q.contest_ids?.length || 0}</td>
                <td className="px-5 py-3.5">{statusBadge(q.status)}</td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => { setEditing(q.question_id); setForm({ ...EMPTY, ...q }); setOpen(true); }}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`question-edit-${q.question_id}`}>
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(q.question_id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`question-delete-${q.question_id}`}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Modifica domanda' : 'Nuova domanda'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Testo della domanda</Label>
              <Textarea className="mt-1.5" rows={2} data-testid="question-text" value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })} />
            </div>
            {form.options.map((o, i) => (
              <div key={i} className="flex items-center gap-3">
                <input type="radio" name="correct" checked={form.correct_index === i}
                  onChange={() => setForm({ ...form, correct_index: i })}
                  data-testid={`question-correct-${i}`} className="accent-brand" />
                <Input value={o} placeholder={`Risposta ${i + 1}`} data-testid={`question-option-${i}`}
                  onChange={(e) => setForm({ ...form, options: form.options.map((x, j) => (j === i ? e.target.value : x)) })} />
              </div>
            ))}
            <p className="text-xs text-slate-400">Seleziona il pallino accanto alla risposta corretta.</p>
            <div>
              <Label>Spiegazione</Label>
              <Textarea className="mt-1.5" rows={2} value={form.explanation}
                onChange={(e) => setForm({ ...form, explanation: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Difficoltà</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="question-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['facile', 'media', 'difficile'].map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Materia</Label>
                <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="question-subject"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => <SelectItem key={s.subject_id} value={s.subject_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Associata ai concorsi</Label>
              <div className="space-y-2 border border-slate-200 rounded-xl p-4 max-h-40 overflow-y-auto">
                {contests.map((c) => (
                  <label key={c.contest_id} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox
                      checked={form.contest_ids.includes(c.contest_id)}
                      onCheckedChange={(checked) => setForm({
                        ...form,
                        contest_ids: checked ? [...form.contest_ids, c.contest_id] : form.contest_ids.filter((x) => x !== c.contest_id),
                      })}
                      data-testid={`question-contest-${c.contest_id}`}
                    />
                    <span className="truncate">{c.title}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="question-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['PUBLISHED', 'DRAFT', 'TO_REVIEW', 'ARCHIVED'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={save} data-testid="question-save" className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white">Salva domanda</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
